import { AfterViewInit, Component, ElementRef, inject, input, signal } from '@angular/core';
import { OverlayRef, OverlaysService, POSITION_TARGET, PositionTarget } from 'app/shared/ui/overlays';


@Component({
    selector: 'ui-tooltip',
    host: {
        'animate.enter': 'fade-enter',
        'animate.leave': 'fade-leave',
        'style': '--fade-duration: 0.2s',
        'role': 'tooltip',
        'class': 'shadow',
        '[attr.id]': 'tooltipId()',
        '(click)': 'onClick()',
        '[style.top.px]': 'top()',
        '[style.left.px]': 'left()',
    },
    styles: [
        `
        :host {
            --background-color: var(--normal, black);
            --color: #F2F7FA;
            --arrow-size: 5px;
            --max-width: 200px;
            display: block;
            position: absolute;
            z-index: 1070;
            font-size: 0.75rem;
            word-wrap: break-word;
            opacity: 0.95;
        }
        .tooltip {
            max-width: var(--max-width);
            padding: 0.25rem 0.5rem;
            color: var(--color);
            text-align: center;
            background-color: var(--background-color);
            border-radius: 0.25rem;
        }
        .tooltip::after {
            content: ' ';
            position: absolute;
            border: var(--arrow-size) solid transparent;
        }

        .tooltip.top::after {
            top: 100%;
            left: 50%;
            margin-left: calc(-1 * var(--arrow-size));
            border-top-color: var(--background-color);
        }
        .tooltip.bottom::after {
            bottom: 100%;
            left: 50%;
            margin-left: calc(-1 * var(--arrow-size));
            border-bottom-color: var(--background-color);
        }
        .tooltip.left::after {
            top: 50%;
            left: 100%;
            margin-top: calc(-1 * var(--arrow-size));
            border-left-color: var(--background-color);
        }
        .tooltip.right::after {
            top: 50%;
            right: 100%;
            margin-top: calc(-1 * var(--arrow-size));
            border-right-color: var(--background-color);
        }
        `,
    ],
    template: `
        <div class="tooltip" [class]="arrow()" [innerHTML]="content()"></div>
    `,
})
export class TooltipComponent implements AfterViewInit {

    content = input.required<string>();

    placement = input<'top' | 'bottom' | 'left' | 'right' | null>('top');

    tooltipId = input<string | null>(null);

    left = signal(0);

    top = signal(0);

    arrow = signal<'top' | 'bottom' | 'left' | 'right' | null>(null);

    private overlayRef = inject(OverlayRef);

    private overlaysService = inject(OverlaysService);

    private elementRef = inject(ElementRef);

    private positionTarget: PositionTarget = inject(POSITION_TARGET);

    ngAfterViewInit() {
        this.calcPosition();
    }

    onClick() {
        this.overlayRef.dismiss('click');
    }

    calcPosition() {
        const width = this.elementRef.nativeElement.offsetWidth;
        const height = this.elementRef.nativeElement.offsetHeight;
        const viewRect = this.overlaysService.getBoundingClientRect();
        const hostRect = this.positionTarget.getBoundingClientRect();

        let newLeft;
        let newTop;
        let placement = this.placement();
        switch (placement) {
            case 'top':
            case 'bottom': {
                // keep position in desired placement, flip placement if does not fit
                if (placement === 'top') {
                    newTop = hostRect.top - height;
                    if (newTop < viewRect.top) {
                        newTop = hostRect.bottom;
                        placement = 'bottom';
                    }
                } else {
                    newTop = hostRect.bottom;
                    if (newTop + height > viewRect.bottom) {
                        newTop = hostRect.top - height;
                        placement = 'top';
                    }
                }

                // keep position in the center, fit in the view, hide arrow if overlay is too far from host
                newLeft = hostRect.left + hostRect.width / 2 - width / 2;
                let diffB;
                if ((diffB = viewRect.left - newLeft) > 0 || (diffB = viewRect.right - (newLeft + width)) < 0) {
                    newLeft += diffB;
                    if (Math.abs(diffB) > hostRect.width / 2) {
                        placement = null;
                    }
                }
                break;
            }
            case 'left':
            case 'right':
            default: {
                // keep position in desired placement, flip placement if does not fit
                if (placement === 'left') {
                    newLeft = hostRect.left - width;
                    if (newLeft < viewRect.left) {
                        newLeft = hostRect.right;
                        placement = 'right';
                    }
                } else {
                    newLeft = hostRect.right;
                    if (newLeft + width > viewRect.right) {
                        newLeft = hostRect.left - width;
                        placement = 'left';
                    }
                }

                // keep position in the middle, fit in the view, hide arrow if overlay is too far from host
                newTop = hostRect.top + hostRect.height / 2 - height / 2;
                let diffT;
                if ((diffT = viewRect.top - newTop) > 0 || (diffT = viewRect.bottom - (newTop + height)) < 0) {
                    newTop += diffT;
                    if (Math.abs(diffT) > hostRect.height / 2) {
                        placement = null;
                    }
                }
            }
        }

        // fix arrows (overlay rect was without placement -- arrows)
        if (placement === 'top') {
            newTop -= 10;
        }
        if (placement === 'left') {
            newLeft -= 10;
        }
        if (placement === 'bottom') {
            newTop += 10;
        }
        if (placement === 'right') {
            newLeft += 10;
        }

        this.arrow.set(placement);
        this.left.set(newLeft);
        this.top.set(newTop);
    }
}
